/**
 * SIM-SZENE LEIPZIG – Haupt-App
 * Kern-Loop: Start → Szenario → Entscheidung → Effekte → Nächste Runde / Game Over
 */
import { useState, useEffect } from 'preact/hooks';
import { GameStateProvider, useGameState, hasSavedGame } from './state/gameState.jsx';
import { selectNextScenario } from './state/scenarioSelection.js';
import { GameWindow } from './components/GameWindow.jsx';
import { ScenarioPanel } from './components/ScenarioPanel.jsx';
import { GameOver } from './components/GameOver.jsx';
import { StartScreen } from './components/StartScreen.jsx';
import { initAudio, playClick, playNewScenario, playBGM, playBGMForPhase, stopBGM } from './audio/sound.js';
import { resolveDiceOutcome, optionUsesDice } from './utils/dice.js';

/**
 * Wrapper mit State Provider
 */
export function App() {
  return (
    <GameStateProvider>
      <Game />
    </GameStateProvider>
  );
}

/**
 * Haupt-Spiellogik
 */
function Game() {
  const { state, dispatch } = useGameState();
  const [gameStarted, setGameStarted] = useState(false);
  const [currentScenario, setCurrentScenario] = useState(null);
  
  // Prüfe ob ein Spiel läuft (für Auto-Continue)
  useEffect(() => {
    if (state.round > 1 && !state.gameOver) {
      setGameStarted(true);
    }
  }, []);
  
  // BGM bei Phasenwechsel wechseln
  useEffect(() => {
    if (gameStarted && !state.gameOver && state.phase) {
      playBGMForPhase(state.phase);
    }
  }, [gameStarted, state.gameOver, state.phase]);

  // Neues Szenario laden wenn nötig
  useEffect(() => {
    if (gameStarted && !state.gameOver && !currentScenario) {
      const scenario = selectNextScenario(state);
      setCurrentScenario(scenario);
      dispatch({ type: 'SET_SCENARIO', scenarioId: scenario.id });
      playNewScenario();
    }
  }, [gameStarted, state.gameOver, currentScenario, state.round]);
  
  // Start-Screen: Sound-Preload beim Klick (kurz), dann Spielstart
  if (!gameStarted) {
    const handleStart = async () => {
      await initAudio();
      setGameStarted(true);
    };
    return (
      <StartScreen
        onStart={handleStart}
        onContinue={handleStart}
      />
    );
  }
  
  // Game Over Screen
  if (state.gameOver) {
    stopBGM();
    return (
      <GameWindow round={state.round}>
        <GameOver />
      </GameWindow>
    );
  }
  
  // Entscheidung verarbeiten (inkl. Würfel-Logik)
  const handleChoice = (option) => {
    playClick();
    let effects = option.effects || {};
    let diceRoll = null;
    let diceOutcomeLabel = null;
    if (optionUsesDice(option)) {
      const resolved = resolveDiceOutcome(option.dice);
      effects = resolved.effects;
      diceRoll = resolved.roll;
      diceOutcomeLabel = resolved.outcomeLabel;
    }
    dispatch({
      type: 'APPLY_EFFECTS',
      effects,
      scenarioId: currentScenario.id,
      diceRoll: diceRoll ?? undefined,
      diceOutcomeLabel: diceOutcomeLabel ?? undefined,
    });

    if (option.sets && option.sets.length > 0) {
      const flags = Object.fromEntries(option.sets.map(f => [f, true]));
      dispatch({ type: 'SET_FLAGS', flags });
    }

    // Phasenwechsel bei Pfadentscheidung: pathId = Einstieg Phase 2, advancesPhase = Einstieg Phase 3
    if (option.pathId) {
      dispatch({ type: 'ADVANCE_PHASE', pathId: option.pathId });
    } else if (option.advancesPhase) {
      dispatch({ type: 'ADVANCE_PHASE' });
    }

    dispatch({ type: 'NEXT_ROUND' });
    setCurrentScenario(null);
  };
  
  // Spielbildschirm
  return (
    <GameWindow round={state.round}>
      <ScenarioPanel 
        scenario={currentScenario} 
        onChoice={handleChoice}
      />
    </GameWindow>
  );
}
